
import { useState } from 'react';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { sendMessageToThread } from '../services/openai';
import {
    generateRoadmapPrompt,
    parseAIResponseToRoadmap,
    DynamicRoadmapConfig
} from '../services/dynamicRoadmapGenerator';
import { roadmapContextService } from '../services/roadmapContextService';
import {
    getRetryMessage,
} from '../utils/languageUtils';
import {
    translateRoadmap,
    getRoadmapSuggestionText,
    getRoadmapFallbackText
} from '../utils/chatUtils';

// Define the interface for the hook's parameters
interface UseRoadmapGenerationProps {
    conversationId: string;
    userId?: string;
    threadId: string | null;
    messages: any[]; // Consider using a Message type if available
    setNewMessage: (msg: string) => void;
    onRoadmapViewToggle: (roadmap: any) => void;
}

// Define the return type of the hook
interface UseRoadmapGenerationResult {
    isGeneratingRoadmap: boolean;
    generateDynamicRoadmap: (
        userMessage: string,
        conversationContext: string,
        analysis: {
            roadmapType: string;
            suggestedDuration: number;
            confidence: number;
            difficulty?: 'beginner' | 'intermediate' | 'advanced'
        },
        prefetchedContext?: any
    ) => Promise<void>;
    handleRoadmapTrigger: (module: any) => Promise<void>;
}

export const useRoadmapGeneration = ({
    conversationId,
    userId,
    threadId,
    messages,
    setNewMessage,
    onRoadmapViewToggle
}: UseRoadmapGenerationProps): UseRoadmapGenerationResult => {

    const [isGeneratingRoadmap, setIsGeneratingRoadmap] = useState(false);

    const handleRoadmapTrigger = async (module: any) => {
        if (module.type === 'roadmap') {
            console.log('Roadmap module triggered:', module);
            if (module.isDynamic && module.roadmapData) {
                // Use the dynamically generated roadmap
                console.log('Using dynamic roadmap data:', module.roadmapData);
                console.log('Roadmap category:', module.roadmapData.category);
                onRoadmapViewToggle(module.roadmapData);
            } else {
                // Fallback to template-based roadmap with localization
                const titlePlusDesc = module.title + module.description;

                const templateRoadmap = {
                    id: module.id,
                    title: module.title || getRoadmapFallbackText('title', titlePlusDesc),
                    description: module.description || getRoadmapFallbackText('description', titlePlusDesc),
                    category: 'template',
                    duration: 7,
                    difficulty: 'beginner',
                    ageRange: '2-6',
                    goals: [getRoadmapFallbackText('improveSkills', titlePlusDesc), getRoadmapFallbackText('buildConfidence', titlePlusDesc)],
                    dailyActivities: [],
                    tips: [getRoadmapFallbackText('beConsistent', titlePlusDesc), getRoadmapFallbackText('celebrateProgress', titlePlusDesc)]
                } as DynamicRoadmapConfig;
                onRoadmapViewToggle(templateRoadmap);
            }
        }
    };

    const generateDynamicRoadmap = async (
        userMessage: string,
        conversationContext: string,
        analysis: { roadmapType: string; suggestedDuration: number; confidence: number; difficulty?: 'beginner' | 'intermediate' | 'advanced' },
        prefetchedContext?: any
    ) => {
        const timeoutDuration = 120000; // 2 minutes timeout

        try {
            console.log('Starting dynamic roadmap generation for:', userMessage);
            setIsGeneratingRoadmap(true);

            // Get enhanced context with pet profiles and conversation history
            // Use pre-fetched context if available
            const enhancedContext = prefetchedContext || await roadmapContextService.getRoadmapContext(
                userId || '',
                userMessage,
                conversationContext
            );

            console.log('🔍 Enhanced context:', {
                hasPetContext: enhancedContext.hasPetContext,
                hasConversationHistory: enhancedContext.hasConversationHistory,
                detectedPetName: enhancedContext.detectedPetName,
                petProfilesCount: enhancedContext.petProfiles.length,
                summariesCount: enhancedContext.recentSummaries.length
            });

            // Generate AI prompt for roadmap creation with enhanced context
            const prompt = generateRoadmapPrompt({
                userQuestion: userMessage,
                conversationContext: enhancedContext.conversationContext,
                preferredDuration: analysis.suggestedDuration,
                difficulty: analysis.difficulty || 'beginner'
            });

            console.log('Generated prompt:', prompt);

            // Get AI response for roadmap generation with timeout
            const aiResponse = await Promise.race([
                sendMessageToThread(threadId!, prompt),
                new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('TIMEOUT')), timeoutDuration)
                )
            ]) as string;

            console.log('AI response received:', aiResponse);

            // Parse AI response into roadmap structure
            let dynamicRoadmap = parseAIResponseToRoadmap(aiResponse, {
                userQuestion: userMessage,
                conversationContext: enhancedContext.conversationContext,
                preferredDuration: analysis.suggestedDuration
            });

            console.log('Parsed roadmap:', dynamicRoadmap);

            if (dynamicRoadmap) {
                // Validate that the roadmap content matches the user's question AND language
                const userQuestion = userMessage.toLowerCase();
                const roadmapTitle = dynamicRoadmap.title.toLowerCase();
                const roadmapDesc = dynamicRoadmap.description.toLowerCase();

                // Check if user asked about independence but got sharing content
                const isIndependenceQuestion = userQuestion.includes('independent') || userQuestion.includes('independence');
                const hasSharingContent = roadmapTitle.includes('share') || roadmapTitle.includes('toy') ||
                    roadmapDesc.includes('share') || roadmapDesc.includes('toy');

                // Check if activities are generic (same for all days) or just repeating titles
                const hasGenericActivities = dynamicRoadmap.dailyActivities.length > 1 &&
                    dynamicRoadmap.dailyActivities.every(day =>
                        day.activities.some(activity =>
                            activity.toLowerCase().includes('activity') ||
                            activity.toLowerCase().includes('continue building') ||
                            activity.toLowerCase().includes(day.title.toLowerCase().replace(/day \d+:\s*/i, '')) ||
                            (activity.toLowerCase().includes('day') && activity.toLowerCase().includes('success')) ||
                            (activity.toLowerCase().includes('day') && activity.toLowerCase().includes('boss'))
                        )
                    );

                // Check for language consistency - ensure AI response matches user's language
                const detectedUserLanguage = /[\u4e00-\u9fff]/.test(userMessage) ? 'chinese' :
                    /[áéíóúñü]/.test(userMessage.toLowerCase()) ? 'spanish' :
                        /[àâäéèêëïîôöùûüÿç]/.test(userMessage.toLowerCase()) ? 'french' : 'english';

                // Enhanced language detection - check for mixed languages in ALL content
                const allRoadmapContent = roadmapTitle + roadmapDesc +
                    dynamicRoadmap.goals.join(' ') +
                    dynamicRoadmap.tips.join(' ') +
                    dynamicRoadmap.dailyActivities.map(day => day.title + day.description + day.activities.join(' ')).join(' ');

                const hasMixedLanguage = (detectedUserLanguage === 'chinese' && /[a-zA-Z]/.test(allRoadmapContent)) ||
                    (detectedUserLanguage === 'spanish' && /[a-zA-Z]/.test(allRoadmapContent)) ||
                    (detectedUserLanguage === 'french' && /[a-zA-Z]/.test(allRoadmapContent)) ||
                    (detectedUserLanguage === 'english' && /[\u4e00-\u9fff]/.test(allRoadmapContent));

                if (isIndependenceQuestion && (hasSharingContent || hasGenericActivities)) {
                    console.log('WARNING: Training independence question generated sharing content - regenerating...');

                    // Try to regenerate with a more specific prompt
                    const specificPrompt = `The pet owner specifically asked about training independence. Create a roadmap focused ONLY on:
           - Basic obedience and self-control skills
           - Making independent decisions and choices
           - Taking responsibility for pet behaviors
           - Building confidence and self-reliance in pets
           - Age-appropriate training milestones for dogs/cats

           CRITICAL: Each day must have 3 SPECIFIC, ACTIONABLE activities that pet owners can actually do with their pet.
           Do NOT repeat the day title in the activities. Do NOT use generic descriptions.

           Example for Day 1:
           Day 1: Sit for Success
           • Let your dog choose between 2 toys
           • Practice sitting command independently
           • Try walking on a loose leash together

           Do NOT include any sharing, human-related, or social skills activities.`;

                    const retryResponse = await sendMessageToThread(threadId!, specificPrompt);
                    const retryRoadmap = parseAIResponseToRoadmap(retryResponse, {
                        userQuestion: userMessage,
                        conversationContext,
                        preferredDuration: analysis.suggestedDuration
                    });

                    if (retryRoadmap) {
                        dynamicRoadmap = retryRoadmap;
                        console.log('Successfully regenerated independence-focused roadmap');
                    }
                } else if (hasMixedLanguage) {
                    console.log('WARNING: Mixed language detected - regenerating with stronger language requirement...');

                    // Regenerate with stronger language requirement
                    const languagePrompt = `🚨🚨🚨🚨🚨 CRITICAL LANGUAGE VIOLATION DETECTED: The pet owner asked in ${detectedUserLanguage === 'chinese' ? 'Chinese (中文)' :
                        detectedUserLanguage === 'spanish' ? 'Spanish (Español)' :
                            detectedUserLanguage === 'french' ? 'French (Français)' : 'English'}.
           
           🚨🚨🚨🚨🚨 YOU MUST respond ENTIRELY in that language. ANY mixed language response will be rejected.
           
           🚨🚨🚨🚨🚨 Create a roadmap with:
           - Title in ${detectedUserLanguage === 'chinese' ? 'Chinese' : detectedUserLanguage === 'spanish' ? 'Spanish' : detectedUserLanguage === 'french' ? 'French' : 'English'}
           - Description in ${detectedUserLanguage === 'chinese' ? 'Chinese' : detectedUserLanguage === 'spanish' ? 'Spanish' : detectedUserLanguage === 'french' ? 'French' : 'English'}
           - Daily activities in ${detectedUserLanguage === 'chinese' ? 'Chinese' : detectedUserLanguage === 'spanish' ? 'Spanish' : detectedUserLanguage === 'french' ? 'French' : 'English'}
           - Goals and tips in ${detectedUserLanguage === 'chinese' ? 'Chinese' : detectedUserLanguage === 'spanish' ? 'Spanish' : detectedUserLanguage === 'french' ? 'French' : 'English'}
           
           🚨🚨🚨🚨🚨 ZERO TOLERANCE: NO English text allowed if user asked in another language.
           🚨🚨🚨🚨🚨 If you fail again, this response will be completely rejected.
           🚨🚨🚨🚨🚨 I will check EVERY field for language consistency.`;

                    const retryResponse = await sendMessageToThread(threadId!, languagePrompt);
                    const retryRoadmap = parseAIResponseToRoadmap(retryResponse, {
                        userQuestion: userMessage,
                        conversationContext,
                        preferredDuration: analysis.suggestedDuration
                    });

                    if (retryRoadmap) {
                        dynamicRoadmap = retryRoadmap;
                        console.log('Successfully regenerated language-consistent roadmap');
                    }
                } else {
                    console.log('Roadmap content validation passed - content matches question and language');
                }

                // Detect language for roadmap suggestion text and store it
                const isChinese = /[\u4e00-\u9fff]/.test(userMessage); // changed from newMessage to userMessage as setMessage is not available directly here in same way
                const isSpanish = /[áéíóúñü]/.test(userMessage.toLowerCase());
                const isFrench = /[àâäéèêëïîôöùûüÿç]/.test(userMessage.toLowerCase());

                // Determine the user's language
                let userLanguage: 'zh-CN' | 'es' | 'fr' | 'en' = 'en';
                if (isChinese) userLanguage = 'zh-CN';
                else if (isSpanish) userLanguage = 'es';
                else if (isFrench) userLanguage = 'fr';

                // CLIENT-SIDE TRANSLATION: Translate roadmap content based on user's language
                if (userLanguage !== 'en') {
                    dynamicRoadmap = translateRoadmap(dynamicRoadmap, userLanguage);
                }

                const roadmapSuggestionText = getRoadmapSuggestionText(analysis.suggestedDuration, userLanguage);

                // Create a message with the dynamic roadmap module
                const roadmapMessage = {
                    id: `dynamic-roadmap-${Date.now()}`,
                    content: roadmapSuggestionText,
                    userId: 'assistant',
                    createdAt: new Date(),
                    role: 'assistant' as const,
                    module: {
                        type: 'roadmap',
                        isDynamic: true,
                        roadmapData: dynamicRoadmap,
                        userLanguage: userLanguage // Store the user's language
                    }
                };

                // Add the dynamic roadmap suggestion to the conversation
                await addDoc(collection(db, `conversations/${conversationId}/messages`), roadmapMessage);

                // Update conversation with last message
                await updateDoc(doc(db, `conversations/${conversationId}`), {
                    lastMessage: roadmapMessage.content.substring(0, 100),
                    updatedAt: new Date(),
                });

                console.log('Dynamic roadmap message added successfully');
            } else {
                console.log('Failed to parse AI response into roadmap structure');
                // Send retry message instead of generating fallback roadmap
                const retryMessage = {
                    content: getRetryMessage('api'),
                    userId: 'assistant',
                    createdAt: new Date(),
                    role: 'assistant' as const,
                };

                await addDoc(collection(db, `conversations/${conversationId}/messages`), retryMessage);

                // Update conversation with retry message
                await updateDoc(doc(db, `conversations/${conversationId}`), {
                    lastMessage: retryMessage.content.substring(0, 100),
                    updatedAt: new Date(),
                });
            }
        } catch (error) {
            console.error('Error generating dynamic roadmap:', error);

            // Determine error type and send appropriate retry message
            let errorType: 'timeout' | 'api' | 'network' = 'api';

            if (error instanceof Error) {
                if (error.message === 'TIMEOUT') {
                    errorType = 'timeout';
                } else if (error.message.includes('network') || error.message.includes('fetch')) {
                    errorType = 'network';
                }
            }

            // Send retry message instead of generating fallback roadmap
            const retryMessage = {
                content: getRetryMessage(errorType),
                userId: 'assistant',
                createdAt: new Date(),
                role: 'assistant' as const,
            };

            await addDoc(collection(db, `conversations/${conversationId}/messages`), retryMessage);

            // Update conversation with retry message
            await updateDoc(doc(db, `conversations/${conversationId}`), {
                lastMessage: retryMessage.content.substring(0, 100),
                updatedAt: new Date(),
            });
        } finally {
            setIsGeneratingRoadmap(false);
        }
    };

    return { isGeneratingRoadmap, generateDynamicRoadmap, handleRoadmapTrigger };
};
